<script setup>
import { computed, onBeforeUnmount, reactive, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore, useMapGetter } from 'dashboard/composables/store';
import { useAlert } from 'dashboard/composables';
import SettingsFieldSection from 'dashboard/components-next/Settings/SettingsFieldSection.vue';
import NextButton from 'dashboard/components-next/button/Button.vue';
import TransferScopeSelector from './components/transfer/TransferScopeSelector.vue';
import TransferOptionsForm from './components/transfer/TransferOptionsForm.vue';
import TransferPreviewSummary from './components/transfer/TransferPreviewSummary.vue';
import TransferProgressTracker from './components/transfer/TransferProgressTracker.vue';

const props = defineProps({
  inbox: {
    type: Object,
    required: true,
  },
});

const { t } = useI18n();
const store = useStore();

const allInboxes = useMapGetter('inboxes/getInboxes');
const uiFlags = useMapGetter('inboxConversationTransfers/getUIFlags');
const previewResult = useMapGetter(
  'inboxConversationTransfers/getPreviewResult'
);
const currentTransfer = useMapGetter(
  'inboxConversationTransfers/getCurrentTransfer'
);

const targetInboxId = ref(null);

const scope = reactive({ mode: 'all' });
const options = reactive({
  reassign_mode: 'preserve_if_valid',
  auto_assign_open: false,
  preserve_team_if_valid: true,
  preserve_sla: true,
  post_transfer_action: '',
});

const confirmDialog = ref(null);

const sourceInboxId = computed(() => props.inbox?.id);

const targetInboxOptions = computed(() =>
  (allInboxes.value || []).filter(
    inbox => inbox.id && inbox.id !== sourceInboxId.value
  )
);

const targetInbox = computed(() =>
  (allInboxes.value || []).find(inbox => inbox.id === targetInboxId.value)
);

const sameInboxError = computed(
  () => targetInboxId.value && targetInboxId.value === sourceInboxId.value
);

const buildPayload = () => {
  const cleanScope = { mode: scope.mode };
  if (scope.mode === 'selected') {
    cleanScope.conversation_display_ids = Array.isArray(
      scope.conversation_display_ids
    )
      ? scope.conversation_display_ids
      : [];
  }
  if (scope.mode === 'date_range') {
    if (scope.created_after) cleanScope.created_after = scope.created_after;
    if (scope.created_before) cleanScope.created_before = scope.created_before;
  }
  return {
    target_inbox_id: targetInboxId.value,
    scope: cleanScope,
    options: { ...options },
  };
};

const canPreview = computed(
  () =>
    Boolean(targetInboxId.value) &&
    !sameInboxError.value &&
    !uiFlags.value.isPreviewing
);

const hasBlockingErrors = computed(
  () => (previewResult.value?.blocking_errors || []).length > 0
);

const canExecute = computed(
  () =>
    Boolean(previewResult.value?.success) &&
    !hasBlockingErrors.value &&
    Boolean(options.post_transfer_action) &&
    !uiFlags.value.isCreating
);

const previewedConversationCount = computed(
  () =>
    previewResult.value?.summary?.total_conversations ??
    (previewResult.value?.resolved_conversation_ids || []).length
);

const onPreview = async () => {
  if (!canPreview.value) return;
  try {
    await store.dispatch('inboxConversationTransfers/previewTransfer', {
      inboxId: sourceInboxId.value,
      payload: buildPayload(),
    });
  } catch (error) {
    if (!error.response) {
      useAlert(t('INBOX_MGMT.CONVERSATION_TRANSFER.API.PREVIEW_ERROR'));
    }
  }
};

const triggerExecute = async () => {
  if (!confirmDialog.value) return;
  const confirmed = await confirmDialog.value.showConfirmation();
  if (!confirmed) return;
  try {
    await store.dispatch('inboxConversationTransfers/createTransfer', {
      inboxId: sourceInboxId.value,
      payload: buildPayload(),
    });
    useAlert(t('INBOX_MGMT.CONVERSATION_TRANSFER.API.CREATE_SUCCESS'));
  } catch (error) {
    useAlert(
      error.response?.data?.error ||
        t('INBOX_MGMT.CONVERSATION_TRANSFER.API.CREATE_ERROR')
    );
  }
};

const onScopeUpdate = nextScope => {
  Object.keys(scope).forEach(key => {
    if (!(key in nextScope)) delete scope[key];
  });
  Object.assign(scope, nextScope);
  store.dispatch('inboxConversationTransfers/clearPreview');
};

const onOptionsUpdate = nextOptions => {
  Object.assign(options, nextOptions);
  store.dispatch('inboxConversationTransfers/clearPreview');
};

const onTargetInboxChange = event => {
  const value = Number(event.target.value);
  targetInboxId.value = Number.isFinite(value) && value > 0 ? value : null;
  store.dispatch('inboxConversationTransfers/clearPreview');
};

const onDismissProgress = () => {
  store.dispatch('inboxConversationTransfers/clearCurrentTransfer');
};

const confirmMessage = computed(() =>
  t('INBOX_MGMT.CONVERSATION_TRANSFER.EXECUTE.CONFIRM_MESSAGE', {
    count: previewedConversationCount.value || 0,
    targetInboxName: targetInbox.value?.name || '',
  })
);

watch(
  () => sourceInboxId.value,
  () => {
    targetInboxId.value = null;
    Object.keys(scope).forEach(key => delete scope[key]);
    scope.mode = 'all';
    store.dispatch('inboxConversationTransfers/resetState');
  }
);

onBeforeUnmount(() => {
  store.dispatch('inboxConversationTransfers/resetState');
});
</script>

<template>
  <div class="mx-6 max-w-4xl">
    <div class="mb-6">
      <h3 class="text-heading-2 text-n-slate-12">
        {{ $t('INBOX_MGMT.CONVERSATION_TRANSFER.TITLE') }}
      </h3>
      <p class="mt-2 text-body-main text-n-slate-11">
        {{ $t('INBOX_MGMT.CONVERSATION_TRANSFER.DESCRIPTION') }}
      </p>
    </div>

    <SettingsFieldSection
      :label="$t('INBOX_MGMT.CONVERSATION_TRANSFER.FORM.SOURCE_INBOX_LABEL')"
    >
      <input
        type="text"
        :value="props.inbox?.name || ''"
        :aria-label="
          $t('INBOX_MGMT.CONVERSATION_TRANSFER.FORM.SOURCE_INBOX_LABEL')
        "
        class="!mb-0"
        disabled
      />
    </SettingsFieldSection>
    <SettingsFieldSection
      :label="$t('INBOX_MGMT.CONVERSATION_TRANSFER.FORM.TARGET_INBOX_LABEL')"
    >
      <select
        :value="targetInboxId || ''"
        :aria-label="
          $t('INBOX_MGMT.CONVERSATION_TRANSFER.FORM.TARGET_INBOX_LABEL')
        "
        class="!mb-0"
        @change="onTargetInboxChange"
      >
        <option value="" disabled>
          {{
            $t('INBOX_MGMT.CONVERSATION_TRANSFER.FORM.TARGET_INBOX_PLACEHOLDER')
          }}
        </option>
        <option
          v-for="option in targetInboxOptions"
          :key="option.id"
          :value="option.id"
        >
          {{ option.name }}
        </option>
      </select>
      <p
        v-if="sameInboxError"
        class="mt-1 text-xs text-red-700 dark:text-red-300"
      >
        {{ $t('INBOX_MGMT.CONVERSATION_TRANSFER.FORM.SAME_INBOX_ERROR') }}
      </p>
    </SettingsFieldSection>

    <TransferScopeSelector :scope="scope" @update:scope="onScopeUpdate" />

    <TransferOptionsForm :options="options" @update:options="onOptionsUpdate" />

    <div class="flex flex-col gap-6 mt-6 pt-4 border-t border-n-weak">
      <TransferPreviewSummary :preview-result="previewResult" />

      <div class="flex flex-wrap justify-end gap-2">
        <NextButton
          :label="
            previewResult
              ? $t('INBOX_MGMT.CONVERSATION_TRANSFER.PREVIEW.BUTTONS.REFRESH')
              : $t('INBOX_MGMT.CONVERSATION_TRANSFER.PREVIEW.BUTTONS.PREVIEW')
          "
          ghost
          slate
          :disabled="!canPreview"
          :is-loading="uiFlags.isPreviewing"
          @click="onPreview"
        />
        <NextButton
          :label="$t('INBOX_MGMT.CONVERSATION_TRANSFER.EXECUTE.BUTTON')"
          :disabled="!canExecute"
          :is-loading="uiFlags.isCreating"
          @click="triggerExecute"
        />
      </div>

      <TransferProgressTracker
        v-if="currentTransfer"
        :inbox-id="sourceInboxId"
        :transfer-id="currentTransfer.id"
        :target-inbox-id="currentTransfer.target_inbox_id"
        @dismiss="onDismissProgress"
      />
    </div>

    <woot-confirm-modal
      ref="confirmDialog"
      :title="$t('INBOX_MGMT.CONVERSATION_TRANSFER.EXECUTE.CONFIRM_TITLE')"
      :description="confirmMessage"
      :confirm-label="$t('INBOX_MGMT.CONVERSATION_TRANSFER.EXECUTE.CONFIRM_OK')"
      :cancel-label="
        $t('INBOX_MGMT.CONVERSATION_TRANSFER.EXECUTE.CONFIRM_CANCEL')
      "
    />
  </div>
</template>
